import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Input, Button, Tabs, message, Space, Select, Spin, Upload } from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { Service } from '@/apis';
import type { SiteConfigPutDto } from '@/apis';
import RichTextEditor from '@/components/RichTextEditor';
import { getDefaultIconNames, renderIcon } from '@/utils/iconRenderer';
import { MultiImageUpload, uploadFile } from '@/page/Cms/ProductManagement/productShared';
import styles from '@/components/CommonTable/index.module.less';

/** 站点导航/快捷链接路径下拉选项 */
const ROUTE_OPTIONS = [
  { value: '/', label: '首页' },
  { value: '/products', label: '产品列表' },
  { value: '/about', label: '关于我们' },
  { value: '/contact', label: '联系我们' },
  { value: '/news', label: '新闻列表' },
  { value: '/faq', label: '常见问题' },
];

const singleImageValueProps = {
  getValueFromEvent: (urls: string[]) => urls?.[0] ?? '',
  getValueProps: (v: string) => ({ value: v ? [v] : [] }),
};

/** 导航图标下拉选项（带预览） */
const ICON_OPTIONS = [
  { value: '', label: '无' },
  ...getDefaultIconNames().map((name) => ({
    value: name,
    label: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {renderIcon(name)}
        {name}
      </span>
    ),
  })),
];

type ConfigKey = 'site' | 'home' | 'about' | 'contact' | 'product';

interface ConfigItem {
  id?: number;
  configKey: string;
  configValue: string;
}

// site 配置结构
interface SiteConfigValue {
  brandName: string;
  logoText: string;
  logoImage: string;
  navItems: { key: string; label: string; icon: string }[];
  whatsappContact?: string;
  footer: {
    brandName: string;
    description: string;
    quickLinks: { label: string; path: string }[];
    contact: { email: string; phone: string; whatsapp?: string };
    copyright: string;
  };
  theme?: {
    heroBackgroundImage?: string;
    heroGradientStart?: string;
    heroGradientEnd?: string;
  };
}

interface HomeServiceItem {
  icon: string;
  title: string;
  description: string;
}

interface HomeFeaturedProductItem {
  image: string;
  title: string;
  link: string;
}

interface HomeProjectStoryItem {
  image: string;
  label: string;
  link?: string;
}

// home 配置结构
interface HomeConfigValue {
  hero: {
    title: string;
    description: string;
    primaryButton: { text: string; path: string };
    secondaryButton: { text: string; path: string };
  };
  categoriesSection: { title: string; description: string; moreText: string };
  aboutIntro: {
    mediaType: 'image' | 'video';
    mediaUrl: string;
    title: string;
    paragraph1: string;
    paragraph2: string;
    buttonText: string;
    buttonPath: string;
  };
  services: { items: HomeServiceItem[] };
  featuredProducts: {
    eyebrow: string;
    title: string;
    buttonText: string;
    items: HomeFeaturedProductItem[];
  };
  projectStories: {
    title: string;
    description: string;
    items: HomeProjectStoryItem[];
  };
  cta: {
    title: string;
    description: string;
    buttonText: string;
    buttonPath: string;
  };
}

// about 配置结构
interface AboutConfigValue {
  heroImage: string;
  title: string;
  lead: string;
  blocks: { icon: string; title: string; text: string }[];
  storyBlocks: {
    image: string;
    title: string;
    text: string;
    imagePosition: 'left' | 'right';
  }[];
  factoryDisplay: {
    title: string;
    images: string[];
  };
  content?: string;
}

// contact 配置结构
interface ContactConfigValue {
  title: string;
  description: string;
  sidebarTitle: string;
  contact: { email: string; phone: string; address?: string; whatsapp?: string };
  sidebarNote: string;
}

// product 配置结构（产品配置）
interface ProductConfigValue {
  description: string;
}

const defaultSite: SiteConfigValue = {
  brandName: '',
  logoText: '',
  logoImage: '',
  navItems: [],
  footer: {
    brandName: '',
    description: '',
    quickLinks: [],
    contact: { email: '', phone: '', whatsapp: '' },
    copyright: '',
  },
  theme: { heroGradientStart: '#6366f1', heroGradientEnd: '#8b5cf6' },
};

const defaultHome: HomeConfigValue = {
  hero: {
    title: '',
    description: '',
    primaryButton: { text: '', path: '' },
    secondaryButton: { text: '', path: '' },
  },
  categoriesSection: {
    title: 'Product Categories',
    description: 'Browse by category. Choose the right products for your needs.',
    moreText: 'Explore More',
  },
  aboutIntro: {
    mediaType: 'image',
    mediaUrl: '',
    title: 'About Sinolinkway',
    paragraph1:
      'Sinolinkway supplies industrial and home solutions covering small appliances, electrical equipment, hardware and solar products for professionals and households.',
    paragraph2:
      'From sourcing to after-sales support, we help partners get reliable products, competitive lead times and consistent quality across every order.',
    buttonText: 'Explore More',
    buttonPath: '/about',
  },
  services: {
    items: [
      {
        icon: 'TrophyOutlined',
        title: 'Superior Quality',
        description:
          'Certified materials and finished goods, inspected to keep every shipment consistent.',
      },
      {
        icon: 'CustomerServiceOutlined',
        title: 'Fast Response',
        description:
          'Our professional service team responds quickly to quotes, samples and technical questions.',
      },
      {
        icon: 'StarOutlined',
        title: 'Strict Inspection',
        description:
          'Incoming and outgoing checks on key specs so products arrive ready for your line.',
      },
      {
        icon: 'CarOutlined',
        title: 'Fast Delivery',
        description:
          'Coordinated packing and logistics to shorten lead time from factory to your warehouse.',
      },
    ],
  },
  featuredProducts: {
    eyebrow: 'One-stop wholesale.',
    title: 'FEATURED PRODUCTS',
    buttonText: 'Inquire Now',
    items: [],
  },
  projectStories: {
    title: 'Project Success Stories',
    description: '',
    items: [],
  },
  cta: { title: '', description: '', buttonText: '', buttonPath: '' },
};

const defaultAbout: AboutConfigValue = {
  heroImage: '',
  title: 'About Us',
  lead: '',
  blocks: [],
  storyBlocks: [],
  factoryDisplay: {
    title: 'Factory Display',
    images: [],
  },
  content: '',
};

const defaultContact: ContactConfigValue = {
  title: '',
  description: '',
  sidebarTitle: '',
  contact: { email: '', phone: '', address: '', whatsapp: '' },
  sidebarNote: '',
};

const defaultProduct: ProductConfigValue = {
  description: '',
};

/** 递归合并：用 defaultVal 做基底，parsed 中有则覆盖，嵌套对象再递归一层（数组直接采用 parsed 或 default） */
function deepMergeWithDefaults<T>(defaultVal: T, parsed: T): T {
  if (parsed == null || typeof parsed !== 'object') return defaultVal;
  if (Array.isArray(parsed)) return parsed as T;
  const result = { ...defaultVal } as Record<string, unknown>;
  const src = parsed as Record<string, unknown>;
  for (const key of Object.keys(src)) {
    const def = (defaultVal as Record<string, unknown>)[key];
    const val = src[key];
    if (val != null && typeof val === 'object' && !Array.isArray(val) && def != null && typeof def === 'object' && !Array.isArray(def)) {
      (result as Record<string, unknown>)[key] = deepMergeWithDefaults(def, val as Record<string, unknown>);
    } else if (val !== undefined) {
      (result as Record<string, unknown>)[key] = val;
    }
  }
  return result as T;
}

function parseConfigValue<T>(raw: string, defaultVal: T): T {
  if (!raw || typeof raw !== 'string') return defaultVal;
  try {
    const parsed = JSON.parse(raw) as T;
    return deepMergeWithDefaults(defaultVal, parsed);
  } catch {
    return defaultVal;
  }
}

const SiteConfig: React.FC = () => {
  const [activeKey, setActiveKey] = useState<ConfigKey>('site');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [siteForm] = Form.useForm<SiteConfigValue>();
  const [homeForm] = Form.useForm<HomeConfigValue>();
  const [aboutForm] = Form.useForm<AboutConfigValue>();
  const [contactForm] = Form.useForm<ContactConfigValue>();
  const [productForm] = Form.useForm<ProductConfigValue>();
  const aboutMediaType = Form.useWatch(['aboutIntro', 'mediaType'], homeForm);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Service.ycSiteConfigControllerGet();
      const data = (res as { data?: unknown })?.data;
      const list = Array.isArray(data) ? data : [];
      const map: Record<string, string> = {};
      (list as ConfigItem[]).forEach((item) => {
        map[item.configKey] = item.configValue ?? '';
      });

      const siteVal = parseConfigValue<SiteConfigValue>(map.site ?? '', defaultSite);
      siteForm.setFieldsValue(siteVal);

      const homeVal = parseConfigValue<HomeConfigValue>(map.home ?? '', defaultHome);
      homeForm.setFieldsValue(homeVal);

      const aboutVal = parseConfigValue<AboutConfigValue>(map.about ?? '', defaultAbout);
      aboutForm.setFieldsValue(aboutVal);

      const contactVal = parseConfigValue<ContactConfigValue>(map.contact ?? '', defaultContact);
      contactForm.setFieldsValue(contactVal);

      const productVal = parseConfigValue<ProductConfigValue>(map.product ?? '', defaultProduct);
      productForm.setFieldsValue(productVal);
    } catch {
      message.error('获取站点配置失败');
    } finally {
      setLoading(false);
    }
  }, [siteForm, homeForm, aboutForm, contactForm, productForm]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async (key: ConfigKey) => {
    let configValue: string;
    try {
      if (key === 'site') {
        const values = await siteForm.validateFields();
        const { whatsappContact: _w, ...rest } = values as SiteConfigValue & { whatsappContact?: string };
        configValue = JSON.stringify(rest, null, 0);
      } else if (key === 'home') {
        const values = await homeForm.validateFields();
        configValue = JSON.stringify(values, null, 0);
      } else if (key === 'about') {
        const values = await aboutForm.validateFields();
        configValue = JSON.stringify(values, null, 0);
      } else if (key === 'product') {
        const values = await productForm.validateFields();
        configValue = JSON.stringify(values, null, 0);
      } else {
        const values = await contactForm.validateFields();
        configValue = JSON.stringify(values, null, 0);
      }
    } catch (e) {
      return;
    }
    const body: SiteConfigPutDto = { configKey: key, configValue };
    setSaving(true);
    try {
      await Service.ycSiteConfigControllerPut(body);
      message.success('保存成功');
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const renderSiteForm = () => (
    <Form form={siteForm} layout="vertical" style={{ maxWidth: 720 }}>
      <Form.Item name="brandName" label="品牌名称" rules={[{ required: true }]}>
        <Input placeholder="如 Sinolinkway" />
      </Form.Item>
      <Form.Item name="logoText" label="Logo 文案">
        <Input placeholder="无 logo 图时显示的文案" />
      </Form.Item>
      <Form.Item name="logoImage" label="Logo 图片 URL">
        <Input placeholder="留空则使用 Logo 文案" />
      </Form.Item>
      <Card size="small" title="导航项" style={{ marginBottom: 16 }}>
        <Form.List name="navItems">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...rest }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item {...rest} name={[name, 'key']} rules={[{ required: true }]} noStyle>
                    <Select options={ROUTE_OPTIONS} placeholder="选择路径" style={{ width: 180 }} />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'label']} rules={[{ required: true }]} noStyle>
                    <Input placeholder="显示文案" style={{ width: 100 }} />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'icon']} noStyle>
                    <Select
                      options={ICON_OPTIONS}
                      placeholder="选择图标"
                      style={{ width: 200 }}
                      allowClear
                    />
                  </Form.Item>
                  <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  添加导航项
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Card>

      <Card size="small" title="页脚" style={{ marginBottom: 16 }}>
        <Form.Item name={['footer', 'brandName']} label="页脚品牌名">
          <Input />
        </Form.Item>
        <Form.Item name={['footer', 'description']} label="页脚描述">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name={['footer', 'copyright']} label="版权文案">
          <Input placeholder="© {year} 品牌名. All rights reserved." />
        </Form.Item>
        <Form.Item name={['footer', 'contact', 'email']} label="页脚邮箱">
          <Input />
        </Form.Item>
        <Form.Item name={['footer', 'contact', 'phone']} label="页脚电话">
          <Input />
        </Form.Item>
        <Form.Item name={['footer', 'contact', 'whatsapp']} label="页脚 WhatsApp">
          <Input placeholder="如 +1234567890" />
        </Form.Item>
        <Form.List name={['footer', 'quickLinks']}>
          {(fields, { add, remove }) => (
            <>
              <div style={{ marginBottom: 8 }}>快捷链接</div>
              {fields.map(({ key, name, ...rest }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item {...rest} name={[name, 'label']} noStyle>
                    <Input placeholder="文案" style={{ width: 100 }} />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'path']} noStyle>
                    <Select options={ROUTE_OPTIONS} placeholder="选择路径" style={{ width: 180 }} />
                  </Form.Item>
                  <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                </Space>
              ))}
              <Button type="dashed" onClick={() => add()} size="small" icon={<PlusOutlined />}>
                添加快捷链接
              </Button>
            </>
          )}
        </Form.List>
      </Card>

      <Card size="small" title="主题（Hero 渐变）" style={{ marginBottom: 16 }}>
        <Form.Item name={['theme', 'heroGradientStart']} label="渐变起始色">
          <Input placeholder="#6366f1" />
        </Form.Item>
        <Form.Item name={['theme', 'heroGradientEnd']} label="渐变结束色">
          <Input placeholder="#8b5cf6" />
        </Form.Item>
      </Card>

      <Form.Item style={{ marginTop: 16 }}>
        <Button type="primary" onClick={() => saveConfig('site')} loading={saving}>
          保存站点全局配置
        </Button>
      </Form.Item>
    </Form>
  );

  const renderHomeForm = () => (
    <Form form={homeForm} layout="vertical" style={{ maxWidth: 880 }}>
      <Card size="small" title="Hero 区域" style={{ marginBottom: 16 }}>
        <Form.Item name={['hero', 'title']} label="主标题" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name={['hero', 'description']} label="副标题/描述">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name={['hero', 'primaryButton', 'text']} label="主按钮文案">
          <Input />
        </Form.Item>
        <Form.Item name={['hero', 'primaryButton', 'path']} label="主按钮路径">
          <Select options={ROUTE_OPTIONS} placeholder="选择路径" allowClear />
        </Form.Item>
        <Form.Item name={['hero', 'secondaryButton', 'text']} label="次按钮文案">
          <Input />
        </Form.Item>
        <Form.Item name={['hero', 'secondaryButton', 'path']} label="次按钮路径">
          <Select options={ROUTE_OPTIONS} placeholder="选择路径" allowClear />
        </Form.Item>
      </Card>

      <Card
        size="small"
        title="分类区块"
        extra="卡片配图、副标题、要点请在「分类管理」中配置"
        style={{ marginBottom: 16 }}
      >
        <Form.Item name={['categoriesSection', 'title']} label="区块标题">
          <Input />
        </Form.Item>
        <Form.Item name={['categoriesSection', 'description']} label="区块描述">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name={['categoriesSection', 'moreText']} label="卡片链接文案">
          <Input placeholder="Explore More" />
        </Form.Item>
      </Card>

      <Card size="small" title="公司介绍（左图/视频，右文字）" style={{ marginBottom: 16 }}>
        <Form.Item name={['aboutIntro', 'mediaType']} label="媒体类型">
          <Select
            options={[
              { value: 'image', label: '图片' },
              { value: 'video', label: '视频' },
            ]}
          />
        </Form.Item>
        {aboutMediaType === 'video' ? (
          <Form.Item
            name={['aboutIntro', 'mediaUrl']}
            label="视频"
            extra="可粘贴视频 URL，或直接上传"
          >
            <Input placeholder="https://...mp4" style={{ marginBottom: 8 }} />
          </Form.Item>
        ) : (
          <Form.Item
            name={['aboutIntro', 'mediaUrl']}
            label="配图"
            extra="左侧大图，建议 16:9 或 4:3"
            {...singleImageValueProps}
          >
            <MultiImageUpload maxCount={1} />
          </Form.Item>
        )}
        {aboutMediaType === 'video' && (
          <Form.Item label="上传视频">
            <Upload
              accept="video/*"
              maxCount={1}
              showUploadList={false}
              customRequest={async (opt) => {
                try {
                  const urls = await uploadFile(opt.file as File);
                  const url = urls[0] ?? '';
                  homeForm.setFieldValue(['aboutIntro', 'mediaUrl'], url);
                  opt.onSuccess?.(urls);
                  message.success('视频已上传');
                } catch {
                  message.error('视频上传失败');
                  opt.onError?.(new Error('upload failed'));
                }
              }}
            >
              <Button icon={<UploadOutlined />}>上传视频文件</Button>
            </Upload>
          </Form.Item>
        )}
        <Form.Item name={['aboutIntro', 'title']} label="标题">
          <Input placeholder="About Sinolinkway" />
        </Form.Item>
        <Form.Item name={['aboutIntro', 'paragraph1']} label="正文第一段">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name={['aboutIntro', 'paragraph2']} label="正文第二段">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name={['aboutIntro', 'buttonText']} label="按钮文案">
          <Input placeholder="Explore More" />
        </Form.Item>
        <Form.Item name={['aboutIntro', 'buttonPath']} label="按钮路径">
          <Select options={ROUTE_OPTIONS} placeholder="选择路径" allowClear />
        </Form.Item>
      </Card>

      <Card size="small" title="公司服务简介（深色四列）" style={{ marginBottom: 16 }}>
        <Form.List name={['services', 'items']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...rest }) => (
                <Card key={key} size="small" style={{ marginBottom: 8 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Form.Item {...rest} name={[name, 'icon']} label="图标">
                      <Select options={ICON_OPTIONS} placeholder="选择图标" allowClear />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'title']} label="标题">
                      <Input placeholder="Superior Quality" />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'description']} label="描述">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)}>
                      删除
                    </Button>
                  </Space>
                </Card>
              ))}
              {fields.length < 4 && (
                <Button
                  type="dashed"
                  onClick={() => add({ icon: 'TrophyOutlined', title: '', description: '' })}
                  block
                  icon={<PlusOutlined />}
                >
                  添加服务亮点（最多 4 项）
                </Button>
              )}
            </>
          )}
        </Form.List>
      </Card>

      <Card size="small" title="精选产品 FEATURED PRODUCTS" extra="产品请在「产品管理」中勾选精选" style={{ marginBottom: 16 }}>
        <Form.Item name={['featuredProducts', 'eyebrow']} label="小标题">
          <Input placeholder="One-stop wholesale." />
        </Form.Item>
        <Form.Item name={['featuredProducts', 'title']} label="区块标题">
          <Input placeholder="FEATURED PRODUCTS" />
        </Form.Item>
        <Form.Item name={['featuredProducts', 'buttonText']} label="按钮文案">
          <Input placeholder="Inquire Now" />
        </Form.Item>
      </Card>

      <Card size="small" title="项目成功案例（建议 8 张，点击切换）" style={{ marginBottom: 16 }}>
        <Form.Item name={['projectStories', 'title']} label="区块标题">
          <Input placeholder="Project Success Stories" />
        </Form.Item>
        <Form.Item name={['projectStories', 'description']} label="描述">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.List name={['projectStories', 'items']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...rest }) => (
                <Card key={key} size="small" style={{ marginBottom: 8 }} title={`案例 ${name + 1}`}>
                  <Form.Item
                    {...rest}
                    name={[name, 'image']}
                    label="竖图"
                    extra="建议竖图 3:4"
                    {...singleImageValueProps}
                  >
                    <MultiImageUpload maxCount={1} />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'label']} label="叠加文案">
                    <Input placeholder="如 Museum Lighting" />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'link']} label="跳转链接（可选）">
                    <Input placeholder="/products 或留空" />
                  </Form.Item>
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)}>
                    删除
                  </Button>
                </Card>
              ))}
              {fields.length < 8 && (
                <Button
                  type="dashed"
                  onClick={() => add({ image: '', label: '', link: '' })}
                  block
                  icon={<PlusOutlined />}
                >
                  添加案例图片（最多 8 张）
                </Button>
              )}
            </>
          )}
        </Form.List>
      </Card>

      <Card size="small" title="CTA 区块">
        <Form.Item name={['cta', 'title']} label="标题">
          <Input />
        </Form.Item>
        <Form.Item name={['cta', 'description']} label="描述">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name={['cta', 'buttonText']} label="按钮文案">
          <Input />
        </Form.Item>
        <Form.Item name={['cta', 'buttonPath']} label="按钮路径">
          <Select options={ROUTE_OPTIONS} placeholder="选择路径" allowClear />
        </Form.Item>
      </Card>
      <Form.Item style={{ marginTop: 16 }}>
        <Button type="primary" onClick={() => saveConfig('home')} loading={saving}>
          保存首页配置
        </Button>
      </Form.Item>
    </Form>
  );

  const renderAboutForm = () => (
    <Form form={aboutForm} layout="vertical" style={{ maxWidth: 880 }}>
      <Card size="small" title="页头" style={{ marginBottom: 16 }}>
        <Form.Item
          name="heroImage"
          label="顶部配图"
          extra="标题上方的一张横图，建议 16:9"
          {...singleImageValueProps}
        >
          <MultiImageUpload maxCount={1} />
        </Form.Item>
        <Form.Item name="title" label="页面标题" rules={[{ required: true }]}>
          <Input placeholder="About Us" />
        </Form.Item>
        <Form.Item name="lead" label="简介/导语">
          <Input.TextArea rows={3} />
        </Form.Item>
      </Card>
      <Card size="small" title="特色区块（建议 3 项）" style={{ marginBottom: 16 }}>
        <Form.List name="blocks">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...rest }) => (
                <Card key={key} size="small" style={{ marginBottom: 8 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Form.Item {...rest} name={[name, 'icon']} label="图标">
                      <Select options={ICON_OPTIONS} placeholder="选择图标" style={{ width: 200 }} allowClear />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'title']} label="标题">
                      <Input />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'text']} label="正文">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)}>
                      删除
                    </Button>
                  </Space>
                </Card>
              ))}
              {fields.length < 6 && (
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  添加特色区块
                </Button>
              )}
            </>
          )}
        </Form.List>
      </Card>
      <Card size="small" title="图文介绍（左图右文 / 右图左文）" style={{ marginBottom: 16 }}>
        <Form.List name="storyBlocks">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...rest }) => (
                <Card key={key} size="small" style={{ marginBottom: 8 }} title={`图文 ${name + 1}`}>
                  <Form.Item {...rest} name={[name, 'imagePosition']} label="图片位置">
                    <Select
                      options={[
                        { value: 'left', label: '左图右文' },
                        { value: 'right', label: '右图左文' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    {...rest}
                    name={[name, 'image']}
                    label="配图"
                    extra="建议 4:3"
                    {...singleImageValueProps}
                  >
                    <MultiImageUpload maxCount={1} />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'title']} label="标题">
                    <Input placeholder="OEM/ODM Service" />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'text']} label="正文">
                    <Input.TextArea rows={4} />
                  </Form.Item>
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)}>
                    删除
                  </Button>
                </Card>
              ))}
              <Button
                type="dashed"
                onClick={() =>
                  add({
                    image: '',
                    title: '',
                    text: '',
                    imagePosition: fields.length % 2 === 0 ? 'left' : 'right',
                  })
                }
                block
                icon={<PlusOutlined />}
              >
                添加图文板块
              </Button>
            </>
          )}
        </Form.List>
      </Card>
      <Card size="small" title="工厂展示（建议 6 张）" style={{ marginBottom: 16 }}>
        <Form.Item name={['factoryDisplay', 'title']} label="区块标题">
          <Input placeholder="Factory Display" />
        </Form.Item>
        <Form.Item
          name={['factoryDisplay', 'images']}
          label="工厂图片"
          extra="最多 6 张，建议 16:9 或 4:3"
        >
          <MultiImageUpload maxCount={6} />
        </Form.Item>
      </Card>
      <Form.Item name="content" label="底部富文本（可选）">
        <RichTextEditor placeholder="关于我们的补充正文，支持富文本" minHeight={200} />
      </Form.Item>
      <Form.Item style={{ marginTop: 16 }}>
        <Button type="primary" onClick={() => saveConfig('about')} loading={saving}>
          保存关于我们配置
        </Button>
      </Form.Item>
    </Form>
  );

  const renderProductForm = () => (
    <Form form={productForm} layout="vertical" style={{ maxWidth: 720 }}>
      <Form.Item name="description" label="产品描述">
        <Input.TextArea rows={6} placeholder="产品页或产品列表的描述文案" />
      </Form.Item>
      <Form.Item style={{ marginTop: 16 }}>
        <Button type="primary" onClick={() => saveConfig('product')} loading={saving}>
          保存产品配置
        </Button>
      </Form.Item>
    </Form>
  );

  const renderContactForm = () => (
    <Form form={contactForm} layout="vertical" style={{ maxWidth: 720 }}>
      <Form.Item name="title" label="页面标题" rules={[{ required: true }]}>
        <Input placeholder="Contact Us" />
      </Form.Item>
      <Form.Item name="description" label="页面描述">
        <Input.TextArea rows={2} />
      </Form.Item>
      <Card size="small" title="侧栏" style={{ marginBottom: 16 }}>
        <Form.Item name="sidebarTitle" label="侧栏标题">
          <Input placeholder="Other ways to reach us" />
        </Form.Item>
        <Form.Item name="sidebarNote" label="侧栏说明">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Card>
      <Card size="small" title="联系方式">
        <Form.Item name={['contact', 'email']} label="邮箱">
          <Input />
        </Form.Item>
        <Form.Item name={['contact', 'phone']} label="电话">
          <Input />
        </Form.Item>
        <Form.Item name={['contact', 'address']} label="地址">
          <Input />
        </Form.Item>
        <Form.Item name={['contact', 'whatsapp']} label="WhatsApp">
          <Input placeholder="+1234567890" />
        </Form.Item>
      </Card>
      <Form.Item style={{ marginTop: 16 }}>
        <Button type="primary" onClick={() => saveConfig('contact')} loading={saving}>
          保存联系我们配置
        </Button>
      </Form.Item>
    </Form>
  );

  const tabItems = [
    { key: 'site', label: '站点全局', children: renderSiteForm() },
    { key: 'home', label: '首页', children: renderHomeForm() },
    { key: 'about', label: '关于我们', children: renderAboutForm() },
    { key: 'contact', label: '联系我们', children: renderContactForm() },
    { key: 'product', label: '产品配置', children: renderProductForm() },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        <Spin spinning={loading}>
          <div className={styles.tabsWrap}>
            <Tabs
              activeKey={activeKey}
              onChange={(k) => setActiveKey(k as ConfigKey)}
              items={tabItems}
            />
          </div>
        </Spin>
      </Card>
    </div>
  );
};

export default SiteConfig;
